/**
 * Transactions & reconciliation.
 *
 * The Transactions tab is the money-event feed. `kind`/`status` are server filters (the
 * contract's `adminTxFilterSchema`); the text box narrows the returned page client-side, since
 * the API takes no free-text query. The Reconciliation tab is the *ledger* check — each
 * account's cached running balance against the immutable sum of its postings, flagging any
 * non-zero drift (a silent corruption to catch early). Running reconciliation is gated on
 * `reconciliation:run`.
 *
 * ## The customer column shows the whole number
 *
 * `adminTxRowSchema` publishes `phone` alongside `maskedPhone`, and this page renders the
 * former through `ContactPhone` like every other operator surface. An agent working a stuck
 * payout off this list should not have to open the customer record for a number they are
 * allowed to see; a console that makes them do that is a console whose operator opens the
 * database instead. Where a row genuinely carries only a mask, the chip beside it says so —
 * a mask presented as though it were the number is read down a phone line, the call does not
 * connect, and nothing on the screen ever explains why.
 *
 * The search box matches both, so typing the last four digits of a number an operator was
 * given finds the row whether or not the full value came back.
 *
 * ## And the vendor's own fee, beside the revenue
 *
 * `providerCost` is what an outside party charged **us** for the movement, which is a
 * different number from what the platform charged the customer, and the gap between them is
 * the desk's actual margin. It is on the row rather than only on a detail route precisely so
 * it can be read on the same line as the amount, and the column totals at the foot of the
 * page — see `totalProviderFees` for why a mixed-currency page shows no total rather than a
 * meaningless one.
 *
 * `null` reads as "not captured", never as zero. A network fee is known at broadcast; a PSP's
 * cut is sometimes only known on settlement, and a blank cell that silently means zero on a
 * money page is the kind of quiet lie this console exists to avoid.
 */
import { useMemo, useState } from 'react';

import { messageOf } from '../api/errors.js';
import { useAuth } from '../auth/AuthContext.js';
import { SkeletonTable } from '../components/Skeleton.js';
import { adminCan } from '../contracts/permissions.js';
import { ADMIN_TX_KINDS, ADMIN_TX_STATUSES } from '../contracts/admin-dto.js';
import type { AdminTxKind, AdminTxStatus } from '../contracts/types.js';
import type { TransactionFilters } from '../api/client.js';
import { Badge, Button, Card, EmptyState, ErrorBox, Input, SearchInput, Select } from '../components/ui.js';
import { TransactionDetail } from '../components/TransactionDetail.js';
import { ContactPhone } from '../components/ContactPhone.js';
import { TxStatusBadge } from '../components/badges.js';
import { IconRefresh } from '../components/icons.js';
import { useToast } from '../components/Toast.js';
import { useAsync } from '../hooks/useAsync.js';
import { totalProviderFees } from '../lib/feeGroups.js';
import { humanize, relativeTime } from '../lib/format.js';
import { formatMoney, isNegativeMoney, isZeroMoney } from '../lib/money.js';

type Tab = 'transactions' | 'reconciliation';

export function TransactionsPage(): JSX.Element {
  const { session } = useAuth();
  const [tab, setTab] = useState<Tab>('transactions');

  /*
   * The balance check is its own grant, and the tab did not know that.
   *
   * The page opens on `transactions:read`, which every role holds, but the reconciliation read
   * behind the second tab is `reconciliation:read` — finance, support and superadmin, and
   * deliberately not compliance. So a compliance reviewer pressed a tab that was always shown
   * and got a permission error where a table should be, on a page they are perfectly entitled
   * to the rest of. A control nobody can use should not be a control.
   */
  const canReconcile = session !== null && adminCan(session.me.role, 'reconciliation:read');
  const active: Tab = canReconcile ? tab : 'transactions';

  return (
    <>
      <div className="page-head">
        <h1 className="page-head__title">Transactions</h1>
        <p className="page-head__sub">
          Every movement of money on the platform.
          {canReconcile &&
            ' The balance check finds any account whose running total does not match the entries behind it.'}
        </p>
      </div>

      {canReconcile && (
        <div className="toolbar" role="tablist">
          <Button variant={active === 'transactions' ? 'primary' : 'default'} onClick={() => setTab('transactions')}>
            Money events
          </Button>
          <Button variant={active === 'reconciliation' ? 'primary' : 'default'} onClick={() => setTab('reconciliation')}>
            Balance check
          </Button>
        </div>
      )}

      {active === 'transactions' ? <TransactionsTab /> : <ReconciliationTab />}
    </>
  );
}

function TransactionsTab(): JSX.Element {
  const { api } = useAuth();
  const [kind, setKind] = useState<AdminTxKind | ''>('');
  const [status, setStatus] = useState<AdminTxStatus | ''>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [query, setQuery] = useState('');
  /** The row whose panel is open, or null. Just an id: the panel fetches its own detail. */
  const [openId, setOpenId] = useState<string | null>(null);

  const filters: TransactionFilters = {
    ...(kind !== '' ? { kind } : {}),
    ...(status !== '' ? { status } : {}),
    ...(from !== '' ? { from } : {}),
    ...(to !== '' ? { to } : {}),
  };
  const state = useAsync(() => api.listTransactions(filters), [kind, status, from, to]);

  // Free-text search is a client-side narrowing over the returned page (the API has no `q`).
  const items = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = state.data?.items ?? [];
    if (q === '') return rows;
    // Both spellings of the number, so the last four digits an operator was given find the
    // row whether or not the full value came back on it.
    return rows.filter(
      (t) =>
        (t.reference ?? '').toLowerCase().includes(q) ||
        // A vendor's own identifier is exactly what an operator pastes in here, having copied
        // it out of that vendor's dashboard. A search that only matched our reference sent
        // them back to the database to translate first.
        t.externalRefs.some((ref) => ref.value.toLowerCase().includes(q)) ||
        (t.maskedPhone ?? '').toLowerCase().includes(q) ||
        (t.phone ?? '').toLowerCase().includes(q),
    );
  }, [state.data, query]);

  const vendorTotal = useMemo(() => totalProviderFees(items), [items]);

  return (
    <>
      <div className="toolbar">
        <div className="toolbar__grow">
          <SearchInput value={query} onChange={setQuery} placeholder="Search reference or phone…" />
        </div>
        <Select value={kind} onChange={(e) => setKind(e.target.value as AdminTxKind | '')} aria-label="Filter by kind">
          <option value="">All kinds</option>
          {ADMIN_TX_KINDS.map((k) => (
            <option key={k} value={k}>
              {humanize(k)}
            </option>
          ))}
        </Select>
        <Select value={status} onChange={(e) => setStatus(e.target.value as AdminTxStatus | '')} aria-label="Filter by status">
          <option value="">All statuses</option>
          {ADMIN_TX_STATUSES.map((s) => (
            <option key={s} value={s}>
              {humanize(s)}
            </option>
          ))}
        </Select>
      </div>

      {/*
        The date window, on its own row rather than crammed beside the dropdowns. It is the
        filter an operator reaches for most on a money feed ("what came in yesterday"), and a
        date input squeezed to a hundred pixels is one nobody can read the value of.

        Both ends cover the whole day named, so putting the same date in both returns that
        day. Said in words underneath, because a control that silently means "up to midnight
        this morning" is one that quietly hides today's takings.
      */}
      <div className="toolbar">
        <label className="toolbar__field">
          <span className="muted">From</span>
          <Input type="date" value={from} max={to === '' ? undefined : to} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="toolbar__field">
          <span className="muted">To</span>
          <Input type="date" value={to} min={from === '' ? undefined : from} onChange={(e) => setTo(e.target.value)} />
        </label>
        {(from !== '' || to !== '') && (
          <Button
            onClick={() => {
              setFrom('');
              setTo('');
            }}
          >
            Clear dates
          </Button>
        )}
        <span className="muted">Both days are included.</span>
      </div>

      <Card flush>
        {state.loading ? (
          <SkeletonTable columns={8} rows={8} label="Loading money events" />
        ) : state.error !== null ? (
          <ErrorBox message={state.error} />
        ) : items.length === 0 ? (
          <EmptyState glyph="🧾" title="No transactions match" hint="Adjust the filters above." />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Kind</th>
                  <th>Customer</th>
                  <th className="table__num">Amount</th>
                  {/* What the vendor charged us, beside what we charged them. */}
                  <th className="table__num">Vendor cost</th>
                  <th>Provider</th>
                  <th>Status</th>
                  <th>Reference</th>
                </tr>
              </thead>
              <tbody>
                {items.map((t) => (
                  /*
                    The whole row opens the panel. Keyboard reachable and Enter-activated as
                    well as clickable: an operator working a queue moves down a list with the
                    arrow keys, and a row that only answers a mouse is one they cannot use.
                  */
                  <tr
                    key={t.id}
                    className="table__row--clickable"
                    tabIndex={0}
                    role="button"
                    aria-label={`Open ${humanize(t.kind)} of ${formatMoney(t.amount)}`}
                    onClick={() => setOpenId(t.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setOpenId(t.id);
                      }
                    }}
                  >
                    <td className="muted" title={t.createdAt}>{relativeTime(t.createdAt)}</td>
                    <td style={{ fontWeight: 600 }}>{humanize(t.kind)}</td>
                    <td>
                      {t.userId === null ? (
                        // A house-side event with no owning customer. Said in words rather
                        // than left as a dash, which reads as a missing value.
                        <span className="faint">House</span>
                      ) : (
                        <ContactPhone row={t} copy={false} compact />
                      )}
                    </td>
                    <td className="table__num" style={{ fontWeight: 650 }}>{formatMoney(t.amount)}</td>
                    <td className="table__num">
                      {t.providerCost === null ? (
                        /*
                          Not captured, which is not the same as no fee. A network fee is known
                          at broadcast; a PSP's cut sometimes only on settlement. A blank cell
                          that silently meant zero would be a margin figure nobody could trust.
                        */
                        <span className="faint" title="No vendor cost has been captured for this movement yet.">
                          Not captured
                        </span>
                      ) : (
                        <>
                          <span className="mono">{formatMoney(t.providerCost.amount)}</span>
                          {/*
                            The party that BILLED, which is not always the row's `provider`: a
                            payout can route through one rail and be billed by another after a
                            failover, and attributing a cost to the wrong rail is how a margin
                            report blames the wrong vendor.
                          */}
                          {t.providerCost.vendor !== t.provider && (
                            <div className="faint" style={{ fontSize: 11.5, textTransform: 'capitalize' }}>
                              billed by {t.providerCost.vendor}
                            </div>
                          )}
                        </>
                      )}
                    </td>
                    <td style={{ textTransform: 'capitalize' }}>{t.provider ?? '–'}</td>
                    <td>
                      <TxStatusBadge status={t.status} />
                    </td>
                    <td className="mono faint">
                      {/*
                        Ours first, then every identifier this movement has outside the system.
                        The set differs per rail - a card charge has a gateway id and nothing
                        else, an incoming transfer has that *and* a NIBSS session ID, a crypto
                        deposit has a custodian id and a chain hash - so they are labelled
                        rather than assigned to columns that would be blank six times out of
                        seven.
                        Stacked under our own reference, quieter, because the operator scanning
                        this column is usually looking for ours and only occasionally needs to
                        take one to a vendor's dashboard.
                      */}
                      <div>{t.reference ?? '–'}</div>
                      {t.externalRefs.map((ref) => (
                        <div key={`${ref.label}:${ref.value}`} className="txrefs__extra">
                          <span className="txrefs__label">{ref.label}</span> {ref.value}
                        </div>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
              {/*
                The column's own total, and only when it means something.

                `totalProviderFees` returns null for a page holding costs in more than one
                currency, because adding a naira PSP fee to a BTC network fee produces a figure
                in no currency at all — and printed under a naira symbol it would be a
                confident lie about how much the desk spent. Rows with nothing captured are
                skipped rather than counted as zero, so the caption says how many were added.
              */}
              {vendorTotal !== null && (
                <tfoot>
                  <tr>
                    <td colSpan={4} className="faint">
                      Vendor cost on this page
                      <span className="faint" style={{ fontSize: 11.5 }}>
                        {' '}
                        ({items.filter((t) => t.providerCost !== null).length} of {items.length}{' '}
                        captured)
                      </span>
                    </td>
                    <td className="table__num mono" style={{ fontWeight: 650 }}>
                      {formatMoney(vendorTotal)}
                    </td>
                    <td colSpan={3} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </Card>

      <TransactionDetail id={openId} onClose={() => setOpenId(null)} />
    </>
  );
}

function ReconciliationTab(): JSX.Element {
  const { api, session } = useAuth();
  const toast = useToast();
  const canRun = session !== null && adminCan(session.me.role, 'reconciliation:run');
  const state = useAsync(() => api.reconciliation(), []);
  const [running, setRunning] = useState(false);

  async function run() {
    setRunning(true);
    try {
      const fresh = await api.runReconciliation();
      state.setData(() => fresh);
      toast.success(
        'Balance check complete',
        fresh.totals.inBalance
          ? 'Every account matched exactly.'
          : `${fresh.totals.drifted} account${fresh.totals.drifted === 1 ? '' : 's'} off.`,
      );
    } catch (err) {
      toast.error('Could not run the balance check', messageOf(err));
    } finally {
      setRunning(false);
    }
  }

  if (state.loading) return <SkeletonTable columns={5} rows={6} label="Loading the balance check" />;
  if (state.error !== null) return <ErrorBox message={state.error} />;
  if (state.data === null) return <ErrorBox message="No reconciliation data." />;

  const { rows, totals, runAt } = state.data;

  return (
    <>
      <div className="grid grid--stats" style={{ marginBottom: 16 }}>
        <Card className="stat">
          <div className="stat__label">Accounts examined</div>
          <div className="stat__value">{totals.accounts}</div>
        </Card>
        <Card className="stat">
          <div className="stat__label">Accounts off</div>
          <div className="stat__value" style={{ color: totals.drifted > 0 ? 'var(--danger)' : 'var(--accent)' }}>
            {totals.drifted}
          </div>
        </Card>
        <Card className="stat">
          <div className="stat__label">Totals vs each entry</div>
          <div className="stat__value" style={{ color: totals.inBalance ? 'var(--accent)' : 'var(--danger)' }}>
            {totals.inBalance ? 'In balance' : 'Off'}
          </div>
          <div className="stat__meta">running total vs entries</div>
        </Card>
      </div>

      <Card
        flush
        title="Balance check"
        actions={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span className="faint" style={{ fontSize: 12 }}>ran {relativeTime(runAt)}</span>
            {canRun && (
              <Button size="sm" variant="primary" loading={running} onClick={run}>
                <IconRefresh size={15} /> Run now
              </Button>
            )}
          </div>
        }
      >
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Account</th>
                <th>Owner</th>
                <th>Currency</th>
                <th className="table__num">Cached balance</th>
                <th className="table__num">Recorded total</th>
                <th className="table__num">Difference</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const zero = isZeroMoney(r.drift);
                return (
                  <tr key={r.account.id}>
                    <td>
                      <span style={{ fontWeight: 600 }}>{humanize(r.account.kind)}</span>
                      <div className="mono faint" style={{ fontSize: 11 }}>{r.account.id.slice(0, 8)}…</div>
                    </td>
                    <td>
                      {r.account.userId === null ? (
                        <Badge tone="violet">House</Badge>
                      ) : (
                        <span className="mono faint">{r.account.userId.slice(0, 8)}…</span>
                      )}
                    </td>
                    <td className="mono">{r.account.currency}</td>
                    <td className="table__num">{formatMoney(r.cachedBalance)}</td>
                    <td className="table__num">{formatMoney(r.postingsSum)}</td>
                    <td className={`table__num ${zero ? 'drift-zero' : 'drift-pos'}`}>
                      {zero ? '–' : `${isNegativeMoney(r.drift) ? '' : '+'}${formatMoney(r.drift)}`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}
